import { useCallback, useContext } from 'react';
import { Button, FormGroup, FormText } from 'reactstrap';
import { useQuery } from '@tanstack/react-query';
import { isEmpty } from 'lodash-es';
import { getCounty } from '@s20/common/util/location';
import { httpClient } from '@s20/services/httpClient';
import {
    FlexStartRow, FormCircularProgress, FormContext, FormLabel, Input, SmallOutlineButton,
} from '../../../common/forms/FormElements';
import { CountyLabel } from '../../../uscounties/CountyLabel';
import { ApiRoutes } from '../../../Common';
import { Address } from '../../../address/Address';

export const LocationSelector = ({
    label, name, usCounties, addressFormRef,
    otherFieldName, allowSameAs = true,
}) => {
    const { values } = useContext(FormContext);
    const { jobRequestId } = values;

    const location = values[name];

    const { data: contract, isLoading: contractLoading } = useQuery({
        queryKey: ['contract', values.contractId],
        queryFn: () => httpClient.js(ApiRoutes.contract.byId(values.contractId)),
    });

    const handleAddLocationClick = useCallback(() => {
        const selectedAddress = new Address(contract.countryId, contract.countryCode);

        addressFormRef.current.open(
            { ...selectedAddress, name },
            `Add ${label}`,
            allowSameAs,
            `Also use as ${name === 'meetingLocation' ? 'Job Location' : 'Meeting Location'}`,
            otherFieldName,
            {
                validateOnOpen: !!jobRequestId,
            },
        );
    }, [name, label, contract, addressFormRef, otherFieldName, jobRequestId, allowSameAs]);

    const handleEditLocationClick = useCallback(() => {
        addressFormRef.current.open(
            { ...location, name },
            `Edit ${label}`,
            allowSameAs,
            `Also use as ${name === 'meetingLocation' ? 'Job Location' : 'Meeting Location'}`,
            otherFieldName,
            {
                validateOnOpen: !!jobRequestId,
            },
        );
    }, [addressFormRef, label, location, name, otherFieldName, jobRequestId, allowSameAs]);

    if (contractLoading) {
        return <FormCircularProgress />;
    }

    const countyWithMeetingLocation = getCounty(usCounties, {
        usCountyId: location.usCountyId,
        contractUnionWorkTypeId: contract.contractUnionWorkTypeId,
    });

    const setLocationInputName = `${name}_set`;

    let highlight = false;

    if (!values[setLocationInputName] && values.jobRequestId) {
        highlight = true;
    }

    return (
        <FormGroup>
            <FormLabel
                htmlFor={name}
                label={label}
                required
            />

            {!isEmpty(location) ? (
                <FlexStartRow>
                    <SmallOutlineButton
                        onClick={handleEditLocationClick}
                        label="Edit"
                    />

                    <span className="form-control-plaintext ml-2">
                        {location.address1}
                        {' '}

                        {location.city}
                        {', '}

                        {location.state}
                        {', '}

                        {location.zip}
                        {' '}

                        (
                        <CountyLabel {...countyWithMeetingLocation} />
                        )
                    </span>
                </FlexStartRow>
            ) : (
                <Button
                    type="button"
                    color="secondary"
                    size="sm"
                    onClick={handleAddLocationClick}
                >
                    Add {label}
                </Button>
            )}

            {Boolean(highlight) && (
                <FormText color="danger">
                    Please double-check the location from the job request.
                </FormText>
            )}

            <Input
                name={setLocationInputName}
                hidden
                required
                error={`A ${label} is required`}
            />
        </FormGroup>
    );
};